import LiveFeedback from './LiveFeedback';
import LiveToolbarIcon from "./LiveToolbarIcon";
import LiveSalesTotal from './LiveSalesTotal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import LiveDetailDialog from './LiveDetailDialog';
import { downloadLiveProductSales } from '../../../../Component/tool/live_product_sales_download';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';

export type ProductSale = { productSeq: number; productName: string; optionSeq: number | null; optionName: string | null;
  unitPrice: number; quantity: number; orderCount: number; paidCount: number };

export function rankProductSales(items: ProductSale[]) {
  const totals = new Map<number, number>();
  items.forEach(item => totals.set(item.productSeq, (totals.get(item.productSeq) || 0) + item.quantity));
  return [...totals.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]).map(([seq]) => seq);
}

export default function LiveProductSales({ liveSeq, refreshVersion = 0, rehearsal = false, totalSales, onTotalChange }: {
  liveSeq: number; refreshVersion?: number; rehearsal?: boolean; totalSales?: number | null; onTotalChange?: (total: number | null) => void;
}) {
  const [items, setItems] = useState<ProductSale[] | null>(null);
  const [allProductsOpen, setAllProductsOpen] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const version = useRef(0), pending = useRef<number | null>(null);
  const load = useCallback(async (showLoading = false) => {
    if (!Number.isSafeInteger(liveSeq) || liveSeq < 1) return;
    if (showLoading) setBusy(true);
    if (pending.current !== null) return;
    const request = ++version.current; pending.current = request;
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}orders/live-product-sales`, { params: { live_seq: liveSeq } });
      if (request !== version.current) return;
      if (!Array.isArray(data?.items) || data.items.some((item: ProductSale) => !Number.isSafeInteger(item.productSeq)
        || typeof item.productName !== 'string' || ![item.unitPrice, item.quantity, item.orderCount, item.paidCount].every(value => Number.isFinite(value) && value >= 0)))
        throw new Error('Invalid product sales');
      const total = (data.items as ProductSale[]).reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
      if (!Number.isFinite(total)) throw new Error('Invalid sales total');
      setItems(data.items); onTotalChange?.(total); setError('');
    } catch { if (request === version.current) setError('판매 현황을 불러오지 못했습니다. 잠시 후 자동으로 다시 불러옵니다.'); }
    finally { if (pending.current === request) pending.current = null; if (request === version.current) setBusy(false); }
  }, [liveSeq, onTotalChange]);
  useEffect(() => {
    setItems(null); onTotalChange?.(null); setAllProductsOpen(false); void load(true);
    const refresh = () => { if (!document.hidden) void load(); };
    const timer = window.setInterval(refresh, 5000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => { version.current++; pending.current = null; window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh); };
  }, [load, rehearsal, onTotalChange]);
  useEffect(() => { void load(); }, [load, refreshVersion]);
  const ranked = useMemo(() => rankProductSales(items || []), [items]);
  const visibleItems = useMemo(() => {
    const order = new Map(ranked.map((seq, index) => [seq, index]));
    const products = new Set(ranked.slice(0, 5));
    return (items || []).filter(item => products.has(item.productSeq)).sort((a, b) => order.get(a.productSeq)! - order.get(b.productSeq)!);
  }, [items, ranked]);
  const allItems = useMemo(() => {
    const order = new Map(ranked.map((seq, index) => [seq, index]));
    return [...(items || [])].sort((a, b) => order.get(a.productSeq)! - order.get(b.productSeq)!);
  }, [items, ranked]);
  const download = () => {
    if (items === null) return;
    try {
      downloadLiveProductSales(items, liveSeq);
    } catch { setError('엑셀 파일을 저장하지 못했습니다. 다시 시도해주세요.'); }
  };
  return <><Section aria-label="상품별 판매 현황">
    {totalSales !== undefined && <TotalHeader><LiveSalesTotal total={totalSales} /></TotalHeader>}
    <Header><h2>판매 수량 Top 5</h2>
      <Actions>
        <ExcelButton type="button" aria-label="상품별 판매 현황 엑셀 다운로드" title="엑셀 다운로드" disabled={items === null} onClick={download}>
          <span aria-hidden="true">📥</span>엑셀
        </ExcelButton>
        <SalesAction type="button" disabled={items === null} aria-expanded={allProductsOpen} aria-haspopup="dialog"
          onClick={() => setAllProductsOpen(true)}><LiveToolbarIcon name="list" />전체 상품 보기</SalesAction>
      </Actions>
    </Header>
    {error && !allProductsOpen && <LiveFeedback role="alert">{error}</LiveFeedback>}
    {!items && busy && <p role="status">판매 현황을 불러오는 중입니다.</p>}
    {items && !items.length && <p>아직 판매 내역이 없습니다.</p>}
    {visibleItems.length > 0 && <SalesTable items={visibleItems} id={`product-sales-${liveSeq}`} />}
  </Section>
  {createPortal(<LiveDetailDialog open={allProductsOpen} title="전체 상품 판매 현황" size="large" onClose={() => setAllProductsOpen(false)}>
    {error && <LiveFeedback role="alert">{error}</LiveFeedback>}
    {allItems.length ? <SalesTable items={allItems} id={`all-product-sales-${liveSeq}`} all /> : <p>아직 판매 내역이 없습니다.</p>}
  </LiveDetailDialog>, document.body)}
  </>;
}

function SalesTable({ items, id, all = false }: { items: ProductSale[]; id: string; all?: boolean }) {
  return <TableScroll id={id} $all={all} tabIndex={0} aria-label="상품별 판매 현황 스크롤">
    <Table aria-description="판매 수량은 취소 수량을 제외합니다. 판매가는 주문 당시 가격이며, 같은 상품도 옵션·가격에 따라 나누어 표시합니다.">
      <thead><tr><th scope="col">상품명</th><th scope="col">옵션</th><th scope="col">판매가</th><th scope="col">판매 수량</th></tr></thead>
      <tbody>{items.map(item => <tr key={JSON.stringify([item.productSeq, item.optionSeq, item.optionName, item.unitPrice])}>
        <td>{item.productName}</td><td>{item.optionName || '기본'}</td><td>{item.unitPrice.toLocaleString()}원</td>
        <td>{item.quantity.toLocaleString()}개</td>
      </tr>)}</tbody>
    </Table>
  </TableScroll>;
}

const Section = styled.section`
  min-width: 0; margin-bottom: var(--space-5); padding: var(--live-card-padding-block, var(--space-2)) var(--space-3); border: 1px solid var(--color-border-default);
  border-radius: var(--radius-lg); background: var(--color-bg-surface); color: var(--color-text-primary);
  font-family: var(--font-family-base); font-size: var(--text-body);
  > p { color: var(--color-text-secondary); }
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); flex-wrap: wrap; margin-bottom: var(--space-2);
  h2 { margin: 0; font-size: var(--text-body); font-weight: 600; }
`;
const TotalHeader = styled.div`
  margin-bottom: var(--space-3); padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--color-border-default);
`;
const Actions = styled.div`display: flex; align-items: center; gap: var(--space-1);`;
const SalesAction = styled(AdminActionButton)`
  && { min-height: var(--control-sm); height: var(--control-sm); padding: 0 var(--space-2); font-size: var(--text-caption); }
  @media (pointer: coarse) { && { min-height: var(--control-lg); height: var(--control-lg); } }
`;
const ExcelButton = styled(SalesAction)`
  span { font-size: var(--text-body-lg); line-height: 1; }
`;
const TableScroll = styled.div<{ $all: boolean }>`
  overflow: auto; max-height: ${({ $all }) => $all ? '60dvh' : 'min(40dvh, calc(var(--control-lg) * 7))'}; overscroll-behavior: contain;
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: var(--text-caption);
  thead { position: sticky; top: 0; background: var(--color-bg-surface); }
  th, td { padding: var(--space-2); text-align: left; border-bottom: 1px solid var(--color-border-default); }
  th { color: var(--color-text-secondary); font-weight: 600; white-space: nowrap; }
  td:nth-child(n+3) { white-space: nowrap; font-variant-numeric: tabular-nums; }
  td:first-child, td:nth-child(2) { min-width: calc(var(--control-sm) * 3); overflow-wrap: anywhere; }
`;
